import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Handshake, Compass } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { CtaSection } from "../components/CtaSection";
import { CinematicBreak } from "../components/CinematicBreak";
import { usePageMeta } from "../hooks/usePageMeta";
import { IMAGES, CINEMATIC, NETWORK } from "../data/site";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/files";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Independent",
    text: "Our advice is never tied to a lender, agent or developer. We sit on your side of the table — and only your side.",
  },
  {
    icon: Compass,
    title: "Practical",
    text: "Clear, considered guidance you can act on, explained in plain language at every stage of the journey.",
  },
  {
    icon: Handshake,
    title: "Connected",
    text: "A carefully built network of trusted professionals, introduced personally when you need them.",
  },
];

const About = () => {
  usePageMeta(
    "About Elin's Consultancy | Property Support",
    "A personal, professional approach to property consultancy — independent guidance for tenants, leaseholders and investors."
  );
  const content = useContent();
  const banner = content?.banners?.about ? fileUrl(content.banners.about) : CINEMATIC.wideOak;
  return (
    <Layout>
      <PageHero
        eyebrow="About Elin's Consultancy"
        lines={[
          "A Personal,",
          <>
            <em className="italic font-normal text-gold-soft">Professional</em> Approach
          </>,
        ]}
        text="Elin's Consultancy was founded to make the property journey simpler, clearer and more accessible — for tenants, leaseholders and investors alike."
        image={banner}
        imageAlt="A wide, sunlit living room with oak flooring and full-length glass"
      />

      <section data-testid="about-story" className="bg-cream">
        <div className="wrap-x py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative h-[340px] sm:h-[460px] overflow-hidden">
              <img
                src={IMAGES.loungeSunlit}
                alt="Sunlit lounge with natural window light and soft seating"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <span aria-hidden="true" className="absolute inset-4 border border-cream/50 pointer-events-none" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow text-gold-dark">Why We Exist</p>
            <h2 className="font-serif text-4xl sm:text-[44px] font-medium text-forest leading-[1.08] mt-5 mb-6">
              Property, Made<br />Clearer for Everyone
            </h2>
            <div className="space-y-5 text-[14.5px] leading-[1.8] text-sage max-w-lg">
              <p>
                Property decisions — whether signing a tenancy, questioning a service charge, or buying
                an investment — often come with complexity that few people have time to untangle alone.
              </p>
              <p>
                We provide independent guidance and work with a trusted network of mortgage advisers,
                solicitors, accountants, estate agents and a development company, so that every client
                has the right support at the right moment.
              </p>
              <p>
                The result is a single, coordinated journey: one point of contact, and professionals
                you can rely on from the first conversation onwards.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CinematicBreak
        image={CINEMATIC.officeWide}
        alt="A quiet, wood-panelled consultancy office with a desk and shelving"
        caption="Our Belief"
        quote="Good advice should feel like a conversation with someone who happens to know property inside out."
      />

      <section data-testid="about-values" className="bg-cream-2">
        <SectionHead
          eyebrow="How We Approach Every Client"
          title="Considered, Independent, Connected"
        />
        <div className="wrap-x pb-20 sm:pb-24 grid grid-cols-1 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal
              key={v.title}
              delay={i * 0.12}
              className={`py-8 md:py-0 md:px-11 ${i > 0 ? "border-t md:border-t-0 md:border-l border-forest/15" : ""} ${i === 0 ? "md:pl-0" : ""}`}
            >
              <v.icon size={34} strokeWidth={1.3} className="text-gold" />
              <h3 className="font-serif text-2xl font-semibold text-forest mt-5 mb-2.5">{v.title}</h3>
              <p className="text-[13.5px] text-sage leading-relaxed">{v.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="about-network" className="bg-cream">
        <SectionHead
          eyebrow="Our Professional Network"
          title="The Right Expertise, Introduced Personally"
          text="Over time we have built relationships with professionals we trust — so you don't have to search alone."
        />
        <div className="wrap-x pb-20 sm:pb-24 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-y-10">
          {NETWORK.map((n, i) => (
            <Reveal
              key={n.name}
              delay={i * 0.08}
              className={`px-6 text-center ${i > 0 ? "xl:border-l border-forest/15" : ""}`}
            >
              <p className="font-serif italic text-gold-dark text-lg">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif text-xl font-semibold text-forest mt-2 mb-2">{n.name}</h3>
              <p className="text-xs leading-[1.65] text-sage">{n.text}</p>
            </Reveal>
          ))}
        </div>
        <div className="wrap-x pb-20 text-center">
          <Reveal>
            <Link to="/how-we-work" data-testid="about-process-link" className="link-more">
              See How We Work <ArrowRight size={13} className="text-gold-dark" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </Layout>
  );
};

export default About;
